import React, {Component} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import faqPlus from '../../static/icons/faq-plus.svg';
import faqMin from '../../static/icons/faq-min.svg';
import styled, {css, keyframes, withTheme} from 'styled-components';
import {gaFaqToggle} from '../../googleAnalytics/faq';

const FaqQuestion = styled.div`
  margin: ${props => props.theme?.faq?.questionMargin || '0 0 0 40px'};
  cursor: pointer;

  h3 {
    margin: ${props => props.theme?.faq?.questionTextMargin};
    font-weight: ${props => props.theme?.faq?.questionFontWeight};
    color: ${props => props.theme?.faq?.questionColor};
  }
`;

const FaqAnswer = styled.div`
  font-size: 18px;
  margin: ${props => props.theme?.faq?.answerMargin || '0 0 0 40px'};
  cursor: pointer;
  overflow: hidden;

  transition: max-height 0.25s ease-out;

  p {
    margin: ${props => props.theme?.faq?.answerTextMargin || '0 0 1em 0'};
    color: ${props => props.theme?.faq?.answerColor};
  }

  ${props => props.isExpanded ? null : css`display: none`}
`;

const expandingShadow = keyframes`
  from {
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.5);
  }

  to {
    box-shadow: 0 8px 8px 2px rgba(0, 0, 0, 0);
  }
`;

const FaqBorder = styled.div`
  margin: ${props => props.theme?.faq?.borderMargin || '15px 0'};
  border-bottom: ${props => props.isDark ? '1px solid #000' : (props.theme?.faq?.borderBottom || '1px solid #000')};
  transition: border-color 0.15s ease-out;

  ${props => props.theme?.faq?.showExpandingAnimation && props.isExpanding && css`
    animation: ${expandingShadow} 0.3s ease-out;
  `}
`;

const FaqHeader = styled.h2`
  font-size: ${props => props.theme?.faq?.headerFontSize};
  font-weight: ${props => props.theme?.faq?.headerFontWeight};
  color: ${props => props.theme?.faq?.headerColor};
  margin: ${props => props.theme?.faq?.headerMargin};
`;

const FaqItem = styled.div``;

const FaqIcon = styled.div`
  float: ${props => props.theme?.faq?.iconFloat || 'left'};
  cursor: pointer;

  ${props => props.theme?.faq?.useDropdownIcon && `
    width: 48px;
    text-align: center;

    :after {
      color: ${props.isExpanded ? '#222222' : '#B0B0B0'};
      content: '\\f107';
      font-family: 'FontAwesome';
      font-size: 24px;
      display: inline-block;
      width: 48px;
      height: 48px;
      line-height: 48px;
      margin-top: -12px;
      border-radius: 50%;
      transition: transform 0.3s ease-out, background-color 0.15s ease-out;
      transform: rotate(${props.isExpanded ? '180deg' : '0deg'});
    }

    ${!props.isExpanded && `
      ${FaqItem}:hover &:after {
        background-color: #EEEEEE;
        color: #222222;
      }
    `}
  `}
`;

const FaqComponent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme?.faq?.padding || '1.25em 0.5em 2em 0.5em'};
`;

const FaqContainer = styled.div`
  margin-top: 2.5em;
  background-color: #fff;
`;

class Faq extends Component {
  constructor (props) {
    super(props);

    this.state = {
      expanded: [],
      hoveredIcon: null,
      expandCount: {}
    };

    this.collapse = this.collapse.bind(this);
    this.expand = this.expand.bind(this);
    this.hoverIcon = this.hoverIcon.bind(this);
    this.unhoverIcon = this.unhoverIcon.bind(this);
  }

  hoverIcon (indx) {
    return () => this.setState({hoveredIcon: indx});
  }

  unhoverIcon () {
    this.setState({hoveredIcon: null});
  }

  static propTypes = {
    questions: PropTypes.array,
    closed: PropTypes.bool
  };

  collapse (indx) {
    return () => {
      const newState = {...this.state};

      newState.expanded[indx] = false;

      this.setState(newState);
      gaFaqToggle(this.props.questions[indx], 'close');
    };
  }

  expand (indx) {
    return () => {
      const newState = {...this.state};
      newState.expanded[indx] = true;
      // Bump the expand counter so the border above this question replays its shadow animation.
      newState.expandCount = {...newState.expandCount, [indx]: (newState.expandCount[indx] || 0) + 1};
      this.setState(newState);
      gaFaqToggle(this.props.questions[indx], 'open');
    };
  }

  render () {
    if (_.isEmpty(this.props.questions)) {
      return null;
    }

    const expandedStates = this.props.questions.map((q, i) => {
      const closedByDefault = q.closed ?? this.props.closed;
      return _.get(this.state, ['expanded', i], !closedByDefault);
    });

    // A collapsed question's border above it turns black while its chevron is hovered.
    const borderIsDark = indx => this.state.hoveredIcon === indx && !expandedStates[indx];
    // The border above a question shows an expanding shadow when the user opens it.
    const borderCount = indx => this.state.expandCount[indx] || 0;
    const borderIsExpanding = indx => borderCount(indx) > 0 && expandedStates[indx];

    const faqs = this.props.questions.map((q, i) => {
      const isExpanded = expandedStates[i];

      return <FaqItem key={i} onClick={isExpanded ? this.collapse(i) : this.expand(i)}>
        <FaqIcon isExpanded={isExpanded} aria-label='Inklappen' title='Inklappen' onMouseEnter={this.hoverIcon(i)} onMouseLeave={this.unhoverIcon}>
          {!this.props.theme?.faq?.useDropdownIcon && <img src={isExpanded ? faqMin : faqPlus} width='22' height='22' alt='Inklappen' />}
        </FaqIcon>
        <FaqQuestion>
          <h3>{q.question}</h3>
        </FaqQuestion>
        <FaqAnswer isExpanded={isExpanded} dangerouslySetInnerHTML={{__html: q.answerHtml_txt}} />
        <FaqBorder key={`border-${i + 1}-${borderCount(i + 1)}`} isDark={borderIsDark(i + 1)} isExpanding={borderIsExpanding(i + 1)} />
      </FaqItem>;
    });

    return <FaqContainer>
      <FaqComponent>
        <FaqHeader>Veelgestelde vragen</FaqHeader>
        <FaqBorder key={`border-0-${borderCount(0)}`} isDark={borderIsDark(0)} isExpanding={borderIsExpanding(0)} />

        {faqs}
      </FaqComponent>
    </FaqContainer>;
  }
}

export default withTheme(Faq);
